// ImportSubmissionsModal.jsx
import React, { useState, useRef } from 'react';
import { Upload, X, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import api from '../utils/axiosConfig';

const ImportSubmissionsModal = ({ isOpen, onClose, currentEvent, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setFile(null);
      setImportResults(null);
      setError(null);
    }
  }, [isOpen]);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file.');
      setFile(null);
      return;
    }

    // Validate file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size cannot exceed 10MB.');
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError(null);
    setImportResults(null);
  };

  const handleImport = async () => {
    if (!file || !currentEvent?.id) return;

    setImporting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/events/${currentEvent.id}/submissions/import/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResults(response.data);
            
    } catch (err) {
      console.error('Error importing submissions:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to import submissions. Please try again.');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (!importing) {
      setFile(null);
      setImportResults(null);
      setError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onImportSuccess()
      onClose();
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Import Submissions</h2>
          <button 
            onClick={handleClose}
            disabled={importing}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* File Upload Section */}
        <div className="mb-6">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              disabled={importing}
            />
            
            {!file ? (
              <div>
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Select CSV file to import
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  CSV files only, max 10MB
                </p>
                <button
                  onClick={triggerFileSelect}
                  disabled={importing}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  Choose File
                </button>
              </div>
            ) : (
              <div>
                <FileText className="mx-auto h-12 w-12 text-green-500 mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  {file.name}
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                <div className="flex justify-center space-x-2">
                  <button
                    onClick={triggerFileSelect}
                    disabled={importing}
                    className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                  >
                    Change File
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={importing}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 flex items-center"
                  >
                    {importing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Importing...
                      </>
                    ) : (
                      'Import'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="text-red-800 font-medium">Import Error</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Import Results */}
        {importResults && (
          <div className="mb-6">
            {importResults.success ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-green-800 font-medium">{importResults.message}</p>
                    
                    {/* Import Summary */}
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="bg-white p-2 rounded border">
                        <p className="font-medium text-gray-600">Total</p>
                        <p className="text-lg font-bold text-gray-900">{importResults.totals.total}</p>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <p className="font-medium text-green-600">Created</p>
                        <p className="text-lg font-bold text-green-700">{importResults.totals.created}</p>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <p className="font-medium text-blue-600">Updated</p>
                        <p className="text-lg font-bold text-blue-700">{importResults.totals.updated}</p>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <p className="font-medium text-red-600">Errors</p>
                        <p className="text-lg font-bold text-red-700">{importResults.totals.errors}</p>
                      </div>
                    </div>
                    
                    {/* Errors Section */}
                    {importResults.errors && importResults.errors.length > 0 && (
                      <div className="mt-4">
                        <p className="font-medium text-red-600 mb-2">
                          Errors ({importResults.totals.errors}):
                        </p>
                        <div className="bg-red-50 border border-red-200 rounded p-3 max-h-40 overflow-y-auto">
                          {importResults.errors.map((error, index) => (
                            <div key={index} className="text-sm text-red-700 mb-1">
                              <span className="font-medium">Row {error.row}:</span>
                              <span className="ml-1">{error.error}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-red-800 font-medium">Import Failed</p>
                    <p className="text-red-700 text-sm mt-1">{importResults.error || 'Unknown error occurred'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CSV Format Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">CSV Format Requirements:</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <strong>email</strong> (required) - Must be unique per event</li>
            <li>• <strong>first_name</strong> (required)</li>
            <li>• <strong>last_name</strong> (required)</li>
            <li>• <strong>phone</strong> (optional)</li>
            <li>• <strong>ticket_type</strong>Ticket type ID</li>
            <li>• Any other columns will be stored as additional data</li>
          </ul>
        </div>

        {/* Close Button */}
        <div className="flex justify-end mt-6">
          <button
            onClick={handleClose}
            disabled={importing}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
          >
            {importResults?.success ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportSubmissionsModal;